import { createEffect, createMemo, onCleanup, type Component, ErrorBoundary, createSignal } from "solid-js";
import { useWs, type SessionPeer } from "./ws";
import { useToolClient, type ToolCallOptions } from "./tool-client";
import { isEnvelope } from "./ws-protocol";
import type { NetworkActionType, NetworkActionPayload } from "./network-actions";
import { matchesPluginJobEvent } from "./use-job-events";

/** Handler d'action émis par un panel plugin vanilla vers l'UI SolidJS. */
export type OnPanelAction = (type: NetworkActionType, payload: NetworkActionPayload) => void;

interface PluginPanelApi {
  call: (tool: string, params?: Record<string, unknown>, opts?: ToolCallOptions) => Promise<unknown>;
  onJobEvent: (jobId: string, cb: (payload: Record<string, unknown>) => void, pluginId?: string | null) => () => void;
  onLiveEvent?: (
    family: string,
    type: string,
    opPrefix: string,
    cb: (message: Record<string, unknown>) => void,
  ) => () => void;
  getSessionPeers?: () => SessionPeer[];
  version: string;
  view?: string;
  onAction?: OnPanelAction;
}

interface PanelModule {
  mount: (el: HTMLElement, api: PluginPanelApi) => void;
  unmount?: (el: HTMLElement) => void;
}

// Cache des modules déjà chargés (url → module).
const moduleCache = new Map<string, PanelModule>();

async function loadPanelModule(url: string): Promise<PanelModule | null> {
  if (moduleCache.has(url)) return moduleCache.get(url)!;
  try {
    const mod = (await import(/* @vite-ignore */ url)) as PanelModule;
    if (typeof mod.mount !== "function") return null;
    moduleCache.set(url, mod);
    return mod;
  } catch (err) {
    console.warn("[plugin-panel-host] échec chargement", url, err);
    return null;
  }
}

export interface PluginPanelHostProps {
  pluginId: string;
  view?: string;
  onAction?: OnPanelAction;
}

const PluginPanelContent: Component<PluginPanelHostProps> = (props) => {
  const ws = useWs();
  const toolClient = useToolClient();
  const [loadError, setLoadError] = createSignal<string | null>(null);
  const [mountError, setMountError] = createSignal<string | null>(null);
  const [_retryCount, setRetryCount] = createSignal(0);
  let containerRef!: HTMLDivElement;

  createEffect(() => {
    setLoadError(null);
    setMountError(null);

    const panelPluginId = props.pluginId;
    const version = ws.state.pluginVersions[panelPluginId];
    if (!version) return;

    const url = `/plugin-assets/${encodeURIComponent(panelPluginId)}/${encodeURIComponent(version)}/panel.js`;

    let unmountFn: ((el: HTMLElement) => void) | undefined;

    loadPanelModule(url)
      .then((mod) => {
        if (!mod) {
          setLoadError(`Impossible de charger le module du plugin`);
          return;
        }
        if (!containerRef) return;
        containerRef.innerHTML = "";

        const api: PluginPanelApi = {
          call: (tool, params = {}, opts = {}) =>
            toolClient.call(tool, params as Record<string, unknown>, opts) as Promise<unknown>,
          onJobEvent: (jobId, cb, pluginId) => {
            const targetPluginId = pluginId ?? panelPluginId;
            const off = ws.on(
              (m) => matchesPluginJobEvent(m, jobId, targetPluginId),
              (m) =>
                cb(
                  ((m as { payload?: { event?: Record<string, unknown> } }).payload?.event ?? {}) as Record<
                    string,
                    unknown
                  >,
                ),
            );
            return off;
          },
          onLiveEvent: (family, type, opPrefix, cb) =>
            ws.on(
              (m) => isEnvelope(m) && m.family === family && m.type === type && m.op.startsWith(opPrefix),
              (m) => cb(m as unknown as Record<string, unknown>),
            ),
          getSessionPeers: () => ws.state.peers,
          version,
          view: props.view,
          onAction: props.onAction,
        };

        try {
          mod.mount(containerRef, api);
          unmountFn = mod.unmount;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setMountError(msg);
          return;
        }
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        setLoadError(msg);
      });

    onCleanup(() => {
      if (unmountFn && containerRef) unmountFn(containerRef);
      else if (containerRef) containerRef.innerHTML = "";
    });
  });

  const handleRetry = () => {
    setRetryCount((c) => c + 1);
  };

  const error = createMemo(() => loadError() || mountError());

  return (
    <div>
      {error() && (
        <div
          style={{
            padding: "1rem",
            background: "var(--danger, #fee)",
            color: "var(--text-1, #333)",
            border: "1px solid var(--danger-border, #fcc)",
            "border-radius": "4px",
            "font-size": "0.875rem",
            "line-height": "1.4",
          }}
        >
          <strong>⚠ Erreur du plugin</strong>
          <div style={{ "margin-top": "0.5rem", "word-break": "break-word" }}>{error()}</div>
          <button
            onclick={handleRetry}
            style={{
              "margin-top": "0.5rem",
              padding: "0.5rem 1rem",
              background: "var(--primary, #0066cc)",
              color: "white",
              border: "none",
              "border-radius": "4px",
              cursor: "pointer",
              "font-size": "0.875rem",
            }}
          >
            Réessayer
          </button>
        </div>
      )}
      <div ref={containerRef} style={{ "min-height": "2rem", display: error() ? "none" : "block" }} />
    </div>
  );
};

export const PluginPanelHost: Component<PluginPanelHostProps> = (props) => {
  return (
    <ErrorBoundary
      fallback={(err, reset) => (
        <div
          style={{
            padding: "1rem",
            background: "var(--danger, #fee)",
            color: "var(--text-1, #333)",
            border: "1px solid var(--danger-border, #fcc)",
            "border-radius": "4px",
            "font-size": "0.875rem",
            "line-height": "1.4",
          }}
        >
          <strong>⚠ Le plugin a rencontré une erreur</strong>
          <div style={{ "margin-top": "0.5rem", "word-break": "break-word" }}>
            {err instanceof Error ? err.message : String(err)}
          </div>
          <button
            onclick={reset}
            style={{
              "margin-top": "0.5rem",
              padding: "0.5rem 1rem",
              background: "var(--primary, #0066cc)",
              color: "white",
              border: "none",
              "border-radius": "4px",
              cursor: "pointer",
              "font-size": "0.875rem",
            }}
          >
            Réessayer
          </button>
        </div>
      )}
    >
      <PluginPanelContent {...props} />
    </ErrorBoundary>
  );
};
